/**
 * <s-card-audio> — play button on a product card. Data attributes:
 *   data-src (MP3) or data-youtube (video ID), plus data-artist, data-release,
 *   data-label, data-cover, data-url for the player panel.
 *
 * Every card plays through one shared panel (snippets/card-player.liquid), so
 * starting a card stops the previous one. MP3 and YouTube use the same panel
 * controls: play/pause, seek bar, elapsed and total time. YouTube's own player
 * controls are hidden, but its video stays visible in the panel as YouTube
 * requires. One YouTube player is created ahead of the first tap and reused, so
 * pressing play starts sound inside the tap (mobile browsers block it otherwise).
 * Other <audio>/<video> on the page (e.g. the product page tracklist)
 * pause when a card starts, and the panel pauses when they start.
 */
const audio = new Audio();
audio.preload = 'none';

/** @type {SCardAudio | null} */
let current = null;

/** YouTube IFrame API player for the current YouTube card. @type {any} */
let ytPlayer = null;
let ytPlaying = false;
let ytTimer = 0;

/** @type {Promise<any> | null} */
let ytApi = null;
function loadYouTubeApi() {
  const w = /** @type {any} */ (window);
  if (w.YT?.Player) return Promise.resolve(w.YT);
  if (!ytApi) {
    ytApi = new Promise((resolve) => {
      const previous = w.onYouTubeIframeAPIReady;
      w.onYouTubeIframeAPIReady = () => {
        previous?.();
        resolve(w.YT);
      };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.append(script);
    });
  }
  return ytApi;
}

const panel = () => /** @type {HTMLElement | null} */ (document.querySelector('[data-card-player]'));
/** @param {string} name */
/** A part of the visible layout (mp3 or youtube). @param {string} name */
const part = (name) =>
  /** @type {HTMLElement | null} */ (panel()?.querySelector(`[data-player-layout]:not([hidden]) [data-player-${name}]`) ?? null);
/** @param {string} name */
const parts = (name) => panel()?.querySelectorAll(`[data-player-${name}]`) ?? [];

/** @param {number} seconds */
function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** @param {boolean} playing */
function setPlaying(playing) {
  for (const toggle of parts('toggle')) toggle.setAttribute('aria-pressed', String(playing));
  current?.setPressed(playing);
}

function pausePageMedia() {
  for (const media of document.querySelectorAll('audio, video')) {
    if (media !== audio) /** @type {HTMLMediaElement} */ (media).pause();
  }
}

/* Unified source: current time, duration, play, pause, seek. */
function currentTime() {
  if (current?.isYouTube) return ytPlayer?.getCurrentTime?.() ?? 0;
  return audio.currentTime;
}
function duration() {
  if (current?.isYouTube) return ytPlayer?.getDuration?.() ?? 0;
  return audio.duration;
}
function isPlaying() {
  if (!current) return false;
  return current.isYouTube ? ytPlaying : !audio.paused;
}
function pauseCurrent() {
  if (!current) return;
  if (current.isYouTube) ytPlayer?.pauseVideo?.();
  else audio.pause();
}
function resumeCurrent() {
  if (!current) return;
  pausePageMedia();
  if (current.isYouTube) ytPlayer?.playVideo?.();
  else audio.play().catch(() => setPlaying(false));
}
/** @param {number} seconds */
function seekTo(seconds) {
  if (current?.isYouTube) ytPlayer?.seekTo?.(seconds, true);
  else audio.currentTime = seconds;
  updateProgress();
}

function updateProgress() {
  const total = duration();
  const now = currentTime();
  const ratio = Number.isFinite(total) && total > 0 ? Math.min(now / total, 1) : 0;
  const progress = part('progress');
  if (progress) progress.style.width = `${ratio * 100}%`;
  const time = part('time');
  const totalEl = part('duration');
  if (time) time.textContent = formatTime(now);
  if (totalEl) totalEl.textContent = formatTime(total);
}

function stopYouTubeTimer() {
  clearInterval(ytTimer);
  ytTimer = 0;
}

function clearMedia() {
  audio.pause();
  audio.removeAttribute('src');
  audio.load();
  stopYouTubeTimer();
  // The YouTube player is kept and reused (see warmYouTube); only stop it and hide it.
  ytPlayer?.stopVideo?.();
  ytPlaying = false;
  const video = videoBox();
  if (video) video.hidden = true;
}


/* Mobile: the panel spans the bottom of the screen, so pad the page by its height
   while it is open; otherwise the last cards and the footer stay hidden behind it. */
const mobileQuery = window.matchMedia('(max-width: 767.98px)');
function reservePanelSpace() {
  const root = panel();
  const open = Boolean(root && !root.hidden);
  document.body.style.paddingBottom = open && mobileQuery.matches && root ? `${root.offsetHeight}px` : '';
}
mobileQuery.addEventListener('change', reservePanelSpace);

function closePanel() {
  clearMedia();
  setPlaying(false);
  current = null;
  const root = panel();
  if (root) root.hidden = true;
  reservePanelSpace();
}

/** @param {SCardAudio} card */
function startYouTube(card) {
  const video = videoBox();
  if (!video) return;
  video.hidden = false;
  setPlaying(true);

  /** @param {any} player */
  const play = (player) => {
    player.getIframe().title = [card.dataset.artist, card.dataset.release].filter(Boolean).join(' - ') || 'YouTube';
    player.loadVideoById(card.dataset.youtube);
    updateProgress();
  };

  // Mobile browsers only allow sound when playback starts inside the tap itself,
  // so a player that is already warm starts the video synchronously here.
  if (ytPlayer) {
    play(ytPlayer);
    return;
  }
  warmYouTube()?.then((player) => {
    // Another card may have been picked while the player loaded.
    if (current === card) play(player);
  });
}

/** The panel's video spot. */
const videoBox = () => /** @type {HTMLElement | null} */ (panel()?.querySelector('[data-player-video]') ?? null);

/** @type {Promise<any> | null} */
let ytWarm = null;

/**
 * Creates the one reusable YouTube player inside the (still hidden) panel ahead of
 * the first tap, so pressing play can start the video right away (see startYouTube).
 */
function warmYouTube() {
  if (ytWarm) return ytWarm;
  const video = videoBox();
  if (!video) return null;
  const mount = document.createElement('div');
  mount.style.cssText = 'width:100%;height:100%;';
  video.replaceChildren(mount);

  ytWarm = loadYouTubeApi().then(
    (YT) =>
      new Promise((resolve) => {
        const player = new YT.Player(mount, {
          host: 'https://www.youtube-nocookie.com',
          width: 200,
          height: 200,
          playerVars: { controls: 0, playsinline: 1, rel: 0, modestbranding: 1, disablekb: 1 },
          events: {
            onReady: () => {
              ytPlayer = player;
              resolve(player);
            },
            onStateChange: (/** @type {any} */ event) => {
              if (!current?.isYouTube) return;
              ytPlaying = event.data === YT.PlayerState.PLAYING || event.data === YT.PlayerState.BUFFERING;
              setPlaying(ytPlaying);
              if (ytPlaying) {
                pausePageMedia();
                if (!ytTimer) ytTimer = window.setInterval(updateProgress, 250);
              } else {
                stopYouTubeTimer();
              }
              updateProgress();
            },
          },
        });
      }),
  );
  return ytWarm;
}

/* Warm the player on the visitor's first touch, scroll or pointer move on a page
   with YouTube samples, well before they reach a play button. */
let warmArmed = false;
function armYouTubeWarmup() {
  if (warmArmed) return;
  warmArmed = true;
  const warm = () => {
    for (const type of ['touchstart', 'pointerdown', 'pointermove', 'scroll']) window.removeEventListener(type, warm);
    warmYouTube();
  };
  for (const type of ['touchstart', 'pointerdown', 'pointermove', 'scroll']) window.addEventListener(type, warm, { passive: true });
}

/** @param {SCardAudio} card */
function load(card) {
  const root = panel();
  if (!root) return;
  clearMedia();
  current?.setPressed(false);
  current = card;


  const { artist = '', release = '', label = '', cover = '', url = '#' } = card.dataset;
  const artistEl = part('artist');
  const releaseEl = part('release');
  const labelEl = part('label');
  if (artistEl) artistEl.textContent = artist;
  if (releaseEl) releaseEl.textContent = release;
  if (labelEl) {
    labelEl.textContent = label;
    labelEl.hidden = !label;
  }
  const coverEl = /** @type {HTMLImageElement | null} */ (part('cover'));
  if (coverEl) coverEl.src = cover || 'data:,';
  for (const link of root.querySelectorAll('[data-player-link]')) link.setAttribute('href', url);
  for (const toggle of parts('toggle')) toggle.setAttribute('aria-label', card.button?.getAttribute('aria-label') || '');

  pausePageMedia();
  root.hidden = false;
  reservePanelSpace();
  updateProgress();

  if (card.isYouTube) {
    startYouTube(card);
    return;
  }

  audio.src = card.dataset.src || '';
  setPlaying(true);
  audio.play().catch(() => setPlaying(false));
}

class SCardAudio extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#onClick);
    if (this.isYouTube) armYouTubeWarmup();
    if (current && current !== this && current.dataset.url === this.dataset.url && !current.isConnected) {
      // Re-rendered card (e.g. filtering): keep the pressed state in sync.
      current = this;
      this.setPressed(isPlaying());
    }
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  get isYouTube() {
    return Boolean(this.dataset.youtube);
  }

  /** @param {boolean} pressed */
  setPressed(pressed) {
    this.button?.setAttribute('aria-pressed', String(pressed));
    this.toggleAttribute('data-playing', pressed);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (current === this) {
      if (isPlaying()) pauseCurrent();
      else resumeCurrent();
      return;
    }
    load(this);
  };
}

audio.addEventListener('play', () => current && !current.isYouTube && setPlaying(true));
audio.addEventListener('pause', () => current && !current.isYouTube && setPlaying(false));
audio.addEventListener('ended', () => current && !current.isYouTube && setPlaying(false));
audio.addEventListener('timeupdate', updateProgress);
audio.addEventListener('loadedmetadata', updateProgress);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('[data-player-toggle]')) {
    if (isPlaying()) pauseCurrent();
    else resumeCurrent();
  } else if (target.closest('[data-player-close]')) {
    closePanel();
  }
});

/**
 * Seek bar: press anywhere on the 24px-tall bar to jump, or drag the knob.
 * @param {Element} bar
 * @param {number} clientX
 */
function seekFromPointer(bar, clientX) {
  const total = duration();
  if (!Number.isFinite(total) || total <= 0) return;
  const rect = bar.getBoundingClientRect();
  const rtl = getComputedStyle(bar).direction === 'rtl';
  const x = rtl ? rect.right - clientX : clientX - rect.left;
  seekTo(Math.min(Math.max(x / rect.width, 0), 1) * total);
}

document.addEventListener('pointerdown', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const bar = /** @type {HTMLElement | null} */ (target.closest('[data-player-bar]'));
  if (!bar) return;
  event.preventDefault();
  bar.setPointerCapture(event.pointerId);
  seekFromPointer(bar, event.clientX);
  /** @param {PointerEvent} move */
  const onMove = (move) => seekFromPointer(bar, move.clientX);
  const onEnd = () => {
    bar.removeEventListener('pointermove', onMove);
    bar.removeEventListener('pointerup', onEnd);
    bar.removeEventListener('pointercancel', onEnd);
  };
  bar.addEventListener('pointermove', onMove);
  bar.addEventListener('pointerup', onEnd);
  bar.addEventListener('pointercancel', onEnd);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && current && panel()?.contains(document.activeElement)) closePanel();
});

// Media events don't bubble, so listen in the capture phase.
document.addEventListener(
  'play',
  (event) => {
    if (!(event.target instanceof HTMLMediaElement) || event.target === audio) return;
    pauseCurrent();
  },
  true,
);

if (!customElements.get('s-card-audio')) {
  customElements.define('s-card-audio', SCardAudio);
}
